/**
 * Pets — rare drops off normal wave mobs, unlocked by beating DJ Leggs
 * (SCOPE_Overlords_and_Pets.md). A pet IS the mob that dropped it: same model,
 * shrunk to heel height. Successive drops level it; each level is +1% to its
 * bonus, capped at level 10 (+10%).
 *
 * Design note: pets are a deliberately MULTIPLICATIVE expansion layer with a
 * low ceiling. Same-type bonuses stack additively with each other, then the
 * type total multiplies the run (max +30% with three same-type level-10 pets).
 */

export type PetBonus = 'damage' | 'scrap' | 'components' | 'dropRate' | 'regen';

export interface PetDef {
  /** The enemy model that drops (and IS) this pet. */
  modelId: string;
  name: string;
  bonus: PetBonus;
  flavor: string;
}

export const PET_BONUS_LABEL: Record<PetBonus, string> = {
  damage: 'Damage',
  scrap: 'Scrap',
  components: 'Components',
  dropRate: 'Part drop rate',
  regen: 'Repair rate',
};

/** Boss-only models (WAR-DEN) never drop pets; everything else can. */
export const PET_DEFS: Record<string, PetDef> = {
  'mdl.vacuum': { modelId: 'mdl.vacuum', name: 'Doombah', bonus: 'scrap', flavor: 'Sweeps the battlefield. Keeps what it finds.' },
  'mdl.fridge': { modelId: 'mdl.fridge', name: 'ColdFront', bonus: 'regen', flavor: 'Keeps your coolant chilled and your spirits adequate.' },
  'mdl.mower': { modelId: 'mdl.mower', name: 'Mowdown', bonus: 'damage', flavor: 'Edges. It always wants to do the edges.' },
  'mdl.speaker': { modelId: 'mdl.speaker', name: 'HAL-exa', bonus: 'dropRate', flavor: 'Hears a part hit the ground three waves away.' },
  'mdl.dogbot': { modelId: 'mdl.dogbot', name: 'Goodboy', bonus: 'components', flavor: 'Fetches Components. Will not explain how.' },
  'mdl.greeter': { modelId: 'mdl.greeter', name: 'Greeter', bonus: 'scrap', flavor: 'Welcomes enemies to their going-out-of-business sale.' },
  'mdl.drone': { modelId: 'mdl.drone', name: 'Prime Suspect', bonus: 'damage', flavor: 'Delivers. Mostly violence.' },
};

export const PET_DEF_LIST: PetDef[] = Object.values(PET_DEFS);

/** Per-kill chance the FIRST copy of a pet drops (bosses never do). Each copy
 *  you already own halves the odds of the next: 1/1000 -> 1/2000 -> 1/4000 ...
 *  so leveling one specific pet from drops gets exponentially rarer; the Yard
 *  is the sane path past the first few. See rollPetDrop for the per-level fold. */
export const PET_DROP_BASE_CHANCE = 0.001;
/** Drops (and overlord kill-levels) stop here; the Yard trains further. */
export const PET_DROP_CAP = 10;
/** The absolute level ceiling, reachable only through Get 'Em training. */
export const MAX_PET_LEVEL = 25;
export const MAX_EQUIPPED_PETS = 3;

/** Collection value -> level (1..MAX_PET_LEVEL). */
export function petLevel(drops: number): number {
  return Math.max(0, Math.min(MAX_PET_LEVEL, drops));
}

/** A pet's bonus, as a percentage (+1% per level, so 25 = +25%). */
export function petBonusPct(level: number): number {
  return Math.max(0, Math.min(MAX_PET_LEVEL, level));
}

/** One def lookup across both pet species (wave drops + overlord minis). */
export function anyPetDef(modelId: string): { name: string; bonus: PetBonus; flavor: string } | undefined {
  return PET_DEFS[modelId] ?? OVERLORD_MINI_DEFS[modelId];
}

/**
 * Overlord minis — trophy pets. Defeat an overlord MINI_KILLS_PER_LEVEL times
 * and a pocket-sized copy joins the bench; every further 5 defeats add a
 * level, up to the drop cap (10). Past that, minis train in the Yard like any
 * other pet (Sit. DOWN. to 15, Get 'Em to 25). Mini levels live in
 * pets.collection like everything else; re-fighting beaten overlords is the
 * kill-level engine.
 */
export const MINI_KILLS_PER_LEVEL = 5;

export interface OverlordMiniDef {
  /** The overlord that earns (and IS) this mini; doubles as its rig model id. */
  overlordId: string;
  name: string;
  bonus: PetBonus;
  flavor: string;
}

export const OVERLORD_MINI_DEFS: Record<string, OverlordMiniDef> = {
  'ovl.synth_ceo': { overlordId: 'ovl.synth_ceo', name: 'Synth CFO', bonus: 'scrap', flavor: 'Handles the petty cash. All cash is petty to him now.' },
  'ovl.synth_ceo_exo': { overlordId: 'ovl.synth_ceo_exo', name: 'Exo Jr.', bonus: 'components', flavor: 'He bought a smaller suit.' },
  'ovl.dj_leggs': { overlordId: 'ovl.dj_leggs', name: 'DJ Smol', bonus: 'dropRate', flavor: 'The bass is decorative. The loyalty is load-bearing.' },
  'ovl.sparky_boi': { overlordId: 'ovl.sparky_boi', name: 'Sparky Boi II', bonus: 'damage', flavor: 'Who is a good boy? This one. This one is.' },
  'ovl.sir_loin': { overlordId: 'ovl.sir_loin', name: 'Filet', bonus: 'damage', flavor: 'A smaller cut. Premium grade.' },
  'ovl.sir_cumference': { overlordId: 'ovl.sir_cumference', name: 'Semi Circle', bonus: 'regen', flavor: 'Half the circumference. Twice the composure. Rolls back into shape.' },
  'ovl.sir_pent': { overlordId: 'ovl.sir_pent', name: 'Caboose', bonus: 'scrap', flavor: 'The little engine that could, and does, and will not stop.' },
  'ovl.mechcorp_hq': { overlordId: 'ovl.mechcorp_hq', name: 'Branch Office', bonus: 'components', flavor: 'Middle management, pocket-sized. Files your dividends in triplicate.' },
  'ovl.mechcorp_dc': { overlordId: 'ovl.mechcorp_dc', name: 'Microservice', bonus: 'dropRate', flavor: 'Finally, truth in naming.' },
  'ovl.mechcorp_city': { overlordId: 'ovl.mechcorp_city', name: 'The Suburb', bonus: 'scrap', flavor: 'A cul-de-sac with a grudge. Collects tolls.' },
  'ovl.sol': { overlordId: 'ovl.sol', name: 'Nightlight', bonus: 'damage', flavor: 'Sol, dwarf class. The night shift loves it.' },
};

export const OVERLORD_MINI_LIST: OverlordMiniDef[] = Object.values(OVERLORD_MINI_DEFS);

/** Defeat count -> kill-earned levels: floor(kills/5), capped at the drop
 *  cap. Used by the v20 migration and the dossier progress chips; the live
 *  level itself is pets.collection (kills and training both feed it). */
export function miniKillLevel(kills: number): number {
  if (kills < MINI_KILLS_PER_LEVEL) return 0;
  return Math.min(PET_DROP_CAP, Math.floor(kills / MINI_KILLS_PER_LEVEL));
}
